import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import type { z } from 'zod'

import { errorHandlingSchema } from '@/schemas/workflow-node'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

type ErrorHandling = z.infer<typeof errorHandlingSchema>

interface ErrorHandlingFormProps {
  value: ErrorHandling
  onChange: (value: ErrorHandling) => void
  availableNodes: { id: string; label: string; type: string }[]
  currentNodeId: string
}

const ON_ERROR_OPTIONS = [
  'fail_workflow',
  'continue',
  'use_default_output',
] as const

const BACKOFF_OPTIONS = ['linear', 'exponential'] as const

export function ErrorHandlingForm({
  value,
  onChange,
  availableNodes,
  currentNodeId,
}: ErrorHandlingFormProps) {
  const { t } = useTranslation('workflows')
  const [open, setOpen] = useState(false)

  const filteredNodes = availableNodes.filter((n) => n.id !== currentNodeId)

  const update = (partial: Partial<ErrorHandling>) => {
    onChange({ ...value, ...partial })
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-gray-700/50 bg-white/5 px-4 py-3 text-sm font-semibold hover:bg-white/10 transition-colors">
        <span>{t('flow.sectionErrorHandling')}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-4">
        {/* Retry Count */}
        <div className="space-y-2">
          <Label htmlFor="retry_count">
            {t('flow.errorHandling.retryCount')}
          </Label>
          <Input
            id="retry_count"
            type="number"
            min={0}
            max={5}
            className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            value={value.retry_count}
            onChange={(e) =>
              update({ retry_count: Math.min(5, Math.max(0, Number(e.target.value))) })
            }
          />
        </div>

        {/* Retry Backoff */}
        <div className="space-y-2">
          <Label htmlFor="retry_backoff">
            {t('flow.errorHandling.retryBackoff')}
          </Label>
          <Select
            value={value.retry_backoff}
            onValueChange={(v) =>
              update({ retry_backoff: v as ErrorHandling['retry_backoff'] })
            }
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BACKOFF_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {t(`flow.errorHandling.backoffOptions.${opt}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fallback Node */}
        <div className="space-y-2">
          <Label htmlFor="fallback_node_id">
            {t('flow.errorHandling.fallbackNode')}
          </Label>
          <Select
            value={value.fallback_node_id ?? '__none__'}
            onValueChange={(v) =>
              update({ fallback_node_id: v === '__none__' ? null : v })
            }
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">--</SelectItem>
              {filteredNodes.map((node) => (
                <SelectItem key={node.id} value={node.id}>
                  <span className="flex items-center gap-2">
                    {node.label}
                    <Badge variant="outline" className="text-xs">
                      {node.type}
                    </Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* On Error */}
        <div className="space-y-2">
          <Label htmlFor="on_error">{t('flow.errorHandling.onError')}</Label>
          <Select
            value={value.on_error}
            onValueChange={(v) =>
              update({ on_error: v as ErrorHandling['on_error'] })
            }
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ON_ERROR_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {t(`flow.errorHandling.onErrorOptions.${opt}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Default Output (conditional) */}
        {value.on_error === 'use_default_output' && (
          <div className="space-y-2">
            <Label htmlFor="default_output">
              {t('flow.errorHandling.defaultOutput')}
            </Label>
            <Textarea
              id="default_output"
              className="font-mono bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              placeholder='{"key": "value"}'
              value={value.default_output ?? ''}
              onChange={(e) => update({ default_output: e.target.value || null })}
            />
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
